<template>
  <div class="chat-message" :class="messageClass">
    <!-- Message Bubble -->
    <div class="message-bubble">
      <!-- Message Text -->
      <div v-if="messageBody" class="message-text" v-html="formattedMessage"></div>
      
      <!-- Attachment Preview (Image or Document) -->
      <div v-if="attachmentsList.length" class="attachment-preview-container">
        <div v-for="att in attachmentsList" :key="att.name" class="attachment-item">
          <!-- Image preview with URL -->
          <div v-if="att.isImage && att.url" class="image-preview-wrapper" @click="openZoom(att)">
            <img :src="att.url" :alt="att.name" class="message-image-preview" />
            <div class="image-filename">{{ att.name }}</div>
          </div>
          <!-- Image placeholder for missing URL -->
          <div v-else-if="att.isImage" class="image-placeholder-wrapper">
            <i class="mdi mdi-image-outline placeholder-icon"></i>
            <span class="placeholder-text">{{ att.name }}</span>
          </div>
          <!-- Document link when URL exists -->
          <a v-else-if="att.url" :href="att.url" :download="att.name" class="doc-preview-wrapper file-download-link" target="_blank">
            <i class="mdi mdi-file-document-outline doc-icon"></i>
            <span class="doc-name">{{ att.name }}</span>
            <i class="mdi mdi-download download-icon"></i>
          </a>
          <!-- Fallback for unknown types -->
          <div v-else class="doc-preview-wrapper">
            <i class="mdi mdi-file-document-outline doc-icon"></i>
            <span class="doc-name">{{ att.name }}</span>
          </div>
        </div>
      </div>
      
      <!-- Product Buttons (only if message type is 'product-selection') -->
      <div v-if="message.type === 'product-selection'" class="product-buttons">
        <ProductButton
          v-for="product in message.products"
          :key="product.id"
          :product="product"
          @click="handleProductClick(product)"
        />
      </div>
      
      <!-- Timestamp -->
      <div class="message-time">{{ formattedTime }}</div>
    </div>

    <!-- Image Lightbox Modal -->
    <div v-if="isZoomed && zoomAttachment" class="image-modal-overlay" @click="closeZoom">
      <button class="modal-close-btn" @click.stop="closeZoom">
        <i class="mdi mdi-close"></i>
      </button>
      <img :src="zoomAttachment.url" :alt="zoomAttachment.name" class="modal-image" @click.stop />
      <div class="modal-caption">{{ zoomAttachment.name }}</div>
    </div>
  </div>
</template>

<script>
import ProductButton from './ProductButton.vue'

export default {
  name: 'ChatMessage',
  components: {
    ProductButton
  },
  props: {
    message: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isZoomed: false,
      zoomAttachment: null
    };
  },
  computed: {
    messageClass() {
      return {
        'bot-message': this.message.sender === 'assistant',
        'user-message': this.message.sender === 'user'
      };
    },
    
    formattedTime() {
      if (!this.message.timestamp) return '';
      
      const date = new Date(this.message.timestamp);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    },

    // Render text with line breaks preserved
    formattedMessage() {
      if (!this.messageBody) return '';
      // Escape HTML to prevent XSS, then replace newline with <br/>
      const escaped = this.messageBody
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
      return escaped.replace(/\n/g, '<br/>');
    },

    // Return an array of all attachment objects extracted from the message text
    attachmentsList() {
      const list = [];
      const text = this.message.text || '';
      const attachmentRegex = /📎 Attached file:\s*([^\n]+)/g;
      let match;
      while ((match = attachmentRegex.exec(text)) !== null) {
        let content = match[1].trim();
        let filename = content;
        let url = null;

        let htmlLinkMatch = null;
        const anchorRegex = /<a[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi;
        let anchorMatch;
        const anchors = [];
        while ((anchorMatch = anchorRegex.exec(content)) !== null) {
          anchors.push({ url: anchorMatch[1].trim(), inner: anchorMatch[2].trim() });
        }

        if (anchors.length > 0) {
          // Prefer the last anchor with text content rather than an image-only anchor.
          const textAnchor = anchors.find(a => !/^<img\b/i.test(a.inner));
          const chosenAnchor = textAnchor || anchors[anchors.length - 1];
          htmlLinkMatch = chosenAnchor;
        }

        if (htmlLinkMatch) {
          url = htmlLinkMatch.url;
          filename = htmlLinkMatch.inner.replace(/<[^>]+>/g, '').trim() || filename;
          if (!filename) {
            const imgAltMatch = content.match(/<img[^>]*alt=["']([^"']+)["'][^>]*>/i);
            if (imgAltMatch) {
              filename = imgAltMatch[1].trim();
            }
          }
        }

        const urlSuffixMatch = filename.match(/\((https?:\/\/[^)]+)\)$/i);
        if (urlSuffixMatch) {
          url = url || urlSuffixMatch[1].trim();
          filename = filename.replace(urlSuffixMatch[0], '').trim();
        }

        const urlFromWindow = window.attachmentUrls ? window.attachmentUrls[filename] : null;
        const isImg = /\.(png|jpe?g|gif|webp|svg)$/i.test(filename);
        list.push({ name: filename, isImage: isImg, url: url || urlFromWindow });
      }
      return list;
    },

    // The original message text without attachment markers (used for the bubble text)
    messageBody() {
      const text = this.message.text || '';
      return text
        .replace(/📎 Attached file:[^\n]*/g, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/\n{2,}/g, '\n')
        .trim();
    },

    // Helper for image detection when a specific attachment object is provided
    isImageAttachment(att) {
      if (!att) return false;
      return att.isImage;
    }
  },
  methods: {
    handleProductClick(product) {
      this.$emit('product-selected', {
        productId: product.id,
        productName: product.name
      });
    },
    openZoom(att) {
      this.zoomAttachment = att;
      this.isZoomed = true;
      document.body.style.overflow = 'hidden';
    },
    closeZoom() {
      this.isZoomed = false;
      this.zoomAttachment = null;
      document.body.style.overflow = '';
    }
  },
  beforeDestroy() {
    // Ensure document scroll capability isn't locked after destroy
    document.body.style.overflow = '';
  }
}
</script>

<style scoped>
.chat-message {
  display: flex;
  margin-bottom: 8px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.bot-message {
  justify-content: flex-start;
}

.user-message {
  justify-content: flex-end;
}

.message-bubble {
  max-width: 70%;
  padding: 16px 18px;
  border-radius: 16px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  word-wrap: break-word;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bot-message .message-bubble {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-left-radius: 4px;
}

.user-message .message-bubble {
  background: #f3f4f6;
  color: #111;
  border-bottom-right-radius: 4px;
  max-width: 60%;
}

.message-text {
  font-size: 15px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
}

.bot-message .message-text {
  text-align: left;
}

.user-message .message-text {
  text-align: right;
}

.message-time {
  font-size: 11px;
  opacity: 0.68;
  margin: 0;
  text-align: right;
  align-self: flex-end;
}

.product-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

/* Attachment Preview Container */
.attachment-preview-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
  margin-top: 6px;
  justify-items: start;
}

.attachment-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-items: flex-start;
}

.image-preview-wrapper,
.doc-preview-wrapper,
.image-placeholder-wrapper {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.image-preview-wrapper {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  cursor: pointer;
  background: rgba(255,255,255,0.08);
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  min-height: 220px;
}

.image-preview-wrapper:hover,
.doc-preview-wrapper:hover,
.image-placeholder-wrapper:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}

.image-filename {
  font-size: 12px;
  padding: 6px 10px;
  font-weight: 500;
  text-align: left;
  word-break: break-all;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.bot-message .image-filename {
  background: rgba(0, 0, 0, 0.15);
  color: rgba(255, 255, 255, 0.9);
}

.user-message .image-filename {
  background: rgba(0, 0, 0, 0.03);
  color: #555;
}

.message-image-preview {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
  border-radius: 8px 8px 0 0;
  display: block;
}

/* Fallbacks for non-image or remote/historical attachments */
.image-placeholder-wrapper,
.doc-preview-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  text-align: left;
}

.bot-message .image-placeholder-wrapper,
.bot-message .doc-preview-wrapper {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
}

.user-message .image-placeholder-wrapper,
.user-message .doc-preview-wrapper {
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #333;
}

.file-download-link {
  text-decoration: none;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.file-download-link:hover {
  transform: translateY(-1px);
}

.bot-message .file-download-link:hover {
  background: rgba(255, 255, 255, 0.25);
}

.user-message .file-download-link:hover {
  background: rgba(0, 0, 0, 0.08);
}

.download-icon {
  margin-left: 6px;
  font-size: 16px;
  opacity: 0.8;
  flex-shrink: 0;
}

.doc-icon,
.placeholder-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.doc-name,
.placeholder-text {
  font-weight: 500;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fullscreen Lightbox Modal */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  animation: modal-fade-in 0.2s ease-out;
}

.modal-image {
  max-width: 90%;
  max-height: 80%;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.6);
  animation: img-scale-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: white;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, transform 0.2s;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: scale(1.1);
}

.modal-caption {
  margin-top: 20px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  font-weight: 500;
  max-width: 80%;
  text-align: center;
  word-break: break-all;
  background: rgba(0, 0, 0, 0.4);
  padding: 6px 16px;
  border-radius: 20px;
}

@keyframes modal-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes img-scale-up {
  from {
    transform: scale(0.92);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
</style>